import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView, AnimatePresence, useScroll, useTransform, useSpring, useMotionValueEvent, type MotionValue } from 'framer-motion';
import { Send } from 'lucide-react';
import { DottedLogo } from './DottedLogo';
import { useCopy } from './copy/copyMode';
export function LivesInSlack({
  progress
}: {
  progress?: MotionValue<number>;
}) {
  const copy = useCopy();
  const containerRef = useRef<HTMLDivElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, {
    margin: '-20%',
    once: false
  });
  const internalScroll = useScroll({
    target: containerRef,
    offset: ['start end', 'end start']
  });
  const scrollYProgress = progress || internalScroll.scrollYProgress;
  const [isActive, setIsActive] = useState(false);
  useMotionValueEvent(scrollYProgress, 'change', latest => {
    if (progress) {
      // Kick the conversation off as soon as the section comes into play.
      setIsActive(latest > 0.02);
    }
  });
  useEffect(() => {
    if (!progress) {
      setIsActive(isInView);
    }
  }, [isInView, progress]);
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mediaQuery.matches);
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);
  // Composer interaction — as the section scrolls into view, it plays out a
  // native Slack mention flow: someone types "@gi", a member-picker dropdown
  // opens, Giga is selected, the blue "@Giga" mention drops in, the rest of
  // the action-forward request is typed, then the message is sent.
  const MENTION_TRIGGER = '@gi';
  const REST_MSG = ' pull the Northwind account and draft a renewal brief for the 2pm call';
  type ComposerPhase = 'idle' | 'mention' | 'dropdown' | 'rest' | 'done' | 'sent' | 'thinking' | 'response';
  const [phase, setPhase] = useState<ComposerPhase>('idle');
  const [trigger, setTrigger] = useState('');
  const [rest, setRest] = useState('');
  const typingDone = phase === 'done' || phase === 'sent' || phase === 'thinking' || phase === 'response';
  // Once the message is sent, scroll the thread to the latest message so Giga's
  // reply (and brief) isn't buried beneath the composer.
  // Scroll ONLY the internal chat thread (never the page) so the animation
  // can't yank the whole window back to this section.
  const scrollThreadToEnd = () => {
    const el = threadRef.current;
    if (!el) return;
    el.scrollTo({
      top: el.scrollHeight,
      behavior: reducedMotion ? 'auto' : 'smooth'
    });
  };
  useEffect(() => {
    if (phase === 'sent' || phase === 'thinking' || phase === 'response') {
      scrollThreadToEnd();
      // Re-scroll several times as the response text, its tool chips, and the
      // card's spring scale all settle — otherwise the reply gets clipped
      // mid-sentence because the thread height keeps growing after the first
      // scroll fires. Staggered passes guarantee the full reply lands in view.
      const delays = [120, 350, 650, 1000, 1500];
      const timers = delays.map(d => setTimeout(scrollThreadToEnd, d));
      return () => timers.forEach(clearTimeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, reducedMotion]);
  useEffect(() => {
    if (!isActive) return;
    if (reducedMotion) {
      setTrigger(MENTION_TRIGGER);
      setRest(REST_MSG);
      setPhase('response');
      return;
    }
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    // Priya's @Giga message is shown already sent — no composer typing.
    // We jump straight to the posted message, then play Giga's reply.
    setTrigger(MENTION_TRIGGER);
    setRest(REST_MSG);
    setPhase('sent');
    // Giga is thinking — kept brief so the user gets to the actual result
    // (the drafted brief + sources) almost immediately.
    timeouts.push(setTimeout(() => {
      setPhase('thinking');
      // Giga responds
      timeouts.push(setTimeout(() => {
        setPhase('response');
      }, 450));
    }, 200));
    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [isActive, reducedMotion]);
  // Scroll-linked scale: the chat card starts small and grows as the section
  // travels up through the viewport, shrinking back when scrolling up.
  // We bound the max scale so it doesn't overwhelm the screen and
  // push the header out of view, while still achieving a slight bottom crop.
  const rawScale = useTransform(scrollYProgress, [0, 0.12, 1], [0.92, 1.0, 1.05]);
  const chatScale = useSpring(rawScale, {
    stiffness: 120,
    damping: 24,
    mass: 0.4
  });
  return <section className={`relative w-full overflow-hidden bg-[#FAF8F4] text-[#2B211A] ${progress ? 'h-screen flex flex-col justify-start pt-12 md:pt-28' : 'h-full pt-12 md:pt-32 pb-8 md:pb-16'}`} data-id="element-1616">
      {/* Soft, flowy gradient wash */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(120% 80% at 50% 0%, rgba(249,115,22,0.08) 0%, rgba(250,248,244,0) 55%), radial-gradient(90% 70% at 15% 90%, rgba(5,150,105,0.05) 0%, rgba(250,248,244,0) 60%)'
    }} data-id="element-1617" />

      {/* Dotted Giga logo background accent */}
      <DottedLogo variant="light" opacity={0.6} className="top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] md:w-[1200px] md:h-[1200px]" data-id="element-1618" />

      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-6 md:px-10 flex flex-col h-full" data-id="element-1619">
        <div className="text-center mb-6 md:mb-8 shrink-0" data-id="element-1620">
          <span className="text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-3 md:mb-4 block" data-id="element-1621">
            Works where you work
          </span>
          <h2 className="text-3xl md:text-5xl font-title font-medium tracking-tight leading-[1.1] text-[#2B211A] max-w-5xl mx-auto mb-3 md:mb-4" data-id="element-1622">
            {copy.livesInSlack.headline}
          </h2>
          <p className="text-base md:text-lg text-neutral-500 max-w-2xl mx-auto leading-relaxed" data-id="element-1623">
            Add Giga to any channel and it answers right there — pulling from
            every connected tool, with the whole team in the loop.
          </p>
        </div>

        {/* DESKTOP SCENE (md and up) */}
        <div ref={containerRef} className="relative z-10 mx-auto w-full max-w-[1040px] h-[60vh] min-h-[500px] max-h-[680px] hidden md:block mt-auto" data-id="element-1624">
          {/* Center chat box — anchored to the TOP of the scene so the card
           only ever grows DOWNWARD as it scales. This guarantees it never
           rises up over the eyebrow/headline/subcopy, and its bottom edge
           gets gently cropped by the section overflow at the end. */}
          <div className="absolute inset-0 pointer-events-none flex items-start justify-center z-20 pt-2" data-id="element-1625">
            {/* Scroll-scaled wrapper: grows the halo + chat together on scroll */}
            <motion.div style={{
            scale: reducedMotion ? 1 : chatScale,
            // Top origin so all growth extends downward, cropping at the
            // bottom of the section rather than overlapping the header.
            transformOrigin: 'center top'
          }} className="relative flex items-start justify-center" data-id="element-1626">
              {/* Chat Card — always present. It enters at the small end of the
               scroll-driven scale and grows/locks in as the section scrolls,
               so it's never invisible (no opacity-gated entrance that made it
               look like nothing was there). */}
              <motion.div style={{
              boxShadow: '0 24px 70px -15px rgba(0,0,0,0.18)',
              borderColor: 'rgba(229, 231, 235, 1)'
            }} className="relative pointer-events-auto w-[720px] bg-white rounded-2xl overflow-hidden border" data-id="element-1627">
                <div className="flex items-center justify-between px-4 py-3 bg-neutral-50 border-b border-neutral-100" data-id="element-1628">
                  <div className="flex gap-1.5" data-id="element-1629">
                    <div className="w-3 h-3 rounded-full bg-neutral-300" data-id="element-1630" />
                    <div className="w-3 h-3 rounded-full bg-neutral-300" data-id="element-1631" />
                    <div className="w-3 h-3 rounded-full bg-neutral-300" data-id="element-1632" />
                  </div>
                  <div className="flex items-center gap-1.5 text-neutral-600 text-sm font-medium" data-id="element-1633">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1634">
                      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" fill="#E01E5A" data-id="element-1635" />
                      <path d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.527 2.527 0 0 1 2.521 2.521 2.527 2.527 0 0 1-2.521 2.521H2.522A2.527 2.527 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z" fill="#36C5F0" data-id="element-1636" />
                      <path d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522v-2.521zm-1.272 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.161 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#2EB67D" data-id="element-1637" />
                      <path d="M15.161 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.161 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.272a2.527 2.527 0 0 1-2.52-2.521 2.526 2.526 0 0 1 2.52-2.522h6.317A2.527 2.527 0 0 1 24 15.163a2.528 2.528 0 0 1-2.522 2.521h-6.317z" fill="#ECB22E" data-id="element-1638" />
                    </svg>
                    Slack
                  </div>
                  <div className="w-8" data-id="element-1639" />
                </div>

                <div className="flex h-[580px]" data-id="element-1640">
                  <div className="w-40 shrink-0 bg-[#F6F5F3] text-neutral-600 border-r border-neutral-200/70 flex flex-col py-4" data-id="element-1641">
                    <div className="px-4 font-bold text-[#2B211A] mb-6 flex items-center gap-2" data-id="element-1642">
                      <div className="w-6 h-6 rounded bg-[#2B211A] text-white flex items-center justify-center font-bold text-xs shrink-0" data-id="element-1643">
                        A
                      </div>
                      <span className="truncate" data-id="element-1644">Acme</span>
                    </div>
                    <div className="px-4 text-xs font-bold mb-2 text-neutral-400 uppercase tracking-wider" data-id="element-1645">
                      Channels
                    </div>
                    <div className="flex flex-col text-[13px] font-medium" data-id="element-1646">
                      <div className="px-4 py-1.5 hover:bg-black/[0.04] cursor-pointer transition-colors" data-id="element-1647">
                        # general
                      </div>
                      <div className="px-4 py-1.5 hover:bg-black/[0.04] cursor-pointer transition-colors" data-id="element-1648">
                        # sales
                      </div>
                      <div className="px-4 py-1.5 hover:bg-black/[0.04] cursor-pointer transition-colors" data-id="element-1649">
                        # engineering
                      </div>
                      <div className="px-4 py-1.5 bg-[#2B211A]/[0.06] text-[#2B211A] font-bold cursor-pointer" data-id="element-1650">
                        # northwind
                      </div>
                      <div className="px-4 py-1.5 hover:bg-black/[0.04] cursor-pointer transition-colors" data-id="element-1651">
                        # marketing
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 flex flex-col bg-white" data-id="element-1652">
                    <div className="px-5 py-3.5 border-b border-neutral-100 flex items-center justify-between" data-id="element-1653">
                      <div className="flex items-center gap-2" data-id="element-1654">
                        <div className="w-2.5 h-2.5 rounded-full bg-[#F97316]" data-id="element-1655" />
                        <span className="font-bold text-[#2B211A] text-sm sm:text-base" data-id="element-1656">
                          Giga
                        </span>
                        <span className="text-[10px] sm:text-xs font-bold bg-[#FFEDD5] text-[#EA580C] px-1.5 py-0.5 rounded" data-id="element-1657">
                          APP
                        </span>
                      </div>
                      <span className="text-xs sm:text-sm text-neutral-400" data-id="element-1658">
                        Acme
                      </span>
                    </div>

                    <div ref={threadRef} className="flex-1 p-5 overflow-y-auto relative scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-1659">
                      <div className="flex flex-col gap-5 sm:gap-6" data-id="element-1660">
                        {/* Sent Message */}
                        <AnimatePresence data-id="element-1661">
                          {(phase === 'sent' || phase === 'thinking' || phase === 'response') && <motion.div initial={{
                          opacity: 0,
                          y: 10
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} className="flex gap-3 sm:gap-4" data-id="element-1662">
                              <img src="https://cdn.magicpatterns.com/uploads/74dN9xBfAH2PfDiwLgxxoL/CleanShot_2026-05-20_at_21.29.242x.png" alt="Faith" className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg object-cover shrink-0" data-id="element-1663" />
                              <div className="min-w-0 flex-1" data-id="element-1664">
                                <div className="flex items-baseline gap-2 mb-1" data-id="element-1665">
                                  <span className="font-bold text-[#333333] text-sm sm:text-base" data-id="element-1666">
                                    Faith
                                  </span>
                                  <span className="text-xs text-neutral-400" data-id="element-1667">
                                    1:50 PM
                                  </span>
                                </div>
                                <p className="text-neutral-700 text-sm sm:text-base leading-relaxed" data-id="element-1668">
                                  <span className="text-[#1264A3] bg-[#1264A3]/10 rounded px-1 py-0.5 font-medium mr-1" data-id="element-1669">
                                    @Giga
                                  </span>
                                  {REST_MSG}
                                </p>
                              </div>
                            </motion.div>}
                        </AnimatePresence>

                        {/* Thinking / Response */}
                        <AnimatePresence mode="wait" data-id="element-1670">
                          {phase === 'thinking' && <motion.div key="thinking" initial={{
                          opacity: 0,
                          y: 10
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} exit={{
                          opacity: 0,
                          y: -10
                        }} className="flex gap-3 sm:gap-4" data-id="element-1671">
                              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#EA580C] flex items-center justify-center shrink-0" data-id="element-1672">
                                <img src="https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png" alt="Giga" className="w-5 h-5 sm:w-6 sm:h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-1673" />
                              </div>
                              <div className="min-w-0 flex-1" data-id="element-1674">
                                <div className="flex items-baseline gap-2 mb-1" data-id="element-1675">
                                  <span className="font-bold text-[#333333] text-sm sm:text-base" data-id="element-1676">
                                    Giga
                                  </span>
                                  <span className="text-[10px] sm:text-xs font-bold bg-[#FFEDD5] text-[#EA580C] px-1.5 py-0.5 rounded" data-id="element-1677">
                                    APP
                                  </span>
                                  <span className="text-xs text-neutral-400" data-id="element-1678">
                                    1:50 PM
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5 mt-2" data-id="element-1679">
                                  <motion.div className="w-1.5 h-1.5 bg-neutral-400 rounded-full" animate={{
                                y: [0, -3, 0]
                              }} transition={{
                                duration: 0.6,
                                repeat: Infinity,
                                delay: 0
                              }} data-id="element-1680" />
                                  <motion.div className="w-1.5 h-1.5 bg-neutral-400 rounded-full" animate={{
                                y: [0, -3, 0]
                              }} transition={{
                                duration: 0.6,
                                repeat: Infinity,
                                delay: 0.2
                              }} data-id="element-1681" />
                                  <motion.div className="w-1.5 h-1.5 bg-neutral-400 rounded-full" animate={{
                                y: [0, -3, 0]
                              }} transition={{
                                duration: 0.6,
                                repeat: Infinity,
                                delay: 0.4
                              }} data-id="element-1682" />
                                  <span className="text-xs text-neutral-500 ml-1" data-id="element-1683">
                                    Pulling context from your stack...
                                  </span>
                                </div>
                              </div>
                            </motion.div>}
                          {phase === 'response' && <motion.div key="response" initial={{
                          opacity: 0,
                          y: 10
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} onAnimationComplete={scrollThreadToEnd} className="flex gap-3 sm:gap-4" data-id="element-1684">
                              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#EA580C] flex items-center justify-center shrink-0" data-id="element-1685">
                                <img src="https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png" alt="Giga" className="w-5 h-5 sm:w-6 sm:h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-1686" />
                              </div>
                              <div className="min-w-0 flex-1" data-id="element-1687">
                                <div className="flex items-baseline gap-2 mb-1" data-id="element-1688">
                                  <span className="font-bold text-[#333333] text-sm sm:text-base" data-id="element-1689">
                                    Giga
                                  </span>
                                  <span className="text-[10px] sm:text-xs font-bold bg-[#FFEDD5] text-[#EA580C] px-1.5 py-0.5 rounded" data-id="element-1690">
                                    APP
                                  </span>
                                  <span className="text-xs text-neutral-400" data-id="element-1691">
                                    1:50 PM
                                  </span>
                                </div>

                                <div className="mt-1" data-id="element-1692">
                                  <motion.div initial={{
                                opacity: 0
                              }} animate={{
                                opacity: 1
                              }} transition={{
                                delay: 0.1,
                                duration: 0.4
                              }} className="text-sm sm:text-[15px] text-neutral-800 leading-relaxed mb-3" data-id="element-1693">
                                    I've pulled the Northwind account context
                                    and drafted the renewal brief in Notion.
                                    It's attached to your 2pm call notes, with
                                    the open API risk{' '}
                                    <span className="font-bold" data-id="element-1694">(ENG-402)</span>{' '}
                                    flagged so you can lead with the extension
                                    offer.
                                  </motion.div>

                                  <div className="flex flex-col gap-1.5 pl-3 border-l-[3px] border-[#EA580C]/80" data-id="element-1695">
                                    {[{
                                  name: 'HubSpot',
                                  icon: 'hubspot',
                                  text: 'Pull Northwind account history and renewal date'
                                }, {
                                  name: 'Gmail',
                                  icon: 'gmail',
                                  text: 'Scan recent email threads with Northwind stakeholders'
                                }, {
                                  name: 'Slack',
                                  icon: 'slack',
                                  text: 'Search #acct-northwind for recent context'
                                }, {
                                  name: 'Google Calendar',
                                  icon: 'googlecalendar',
                                  text: 'Check attendees for the 2pm call'
                                }, {
                                  name: 'Notion',
                                  icon: 'notion',
                                  text: 'Draft renewal brief in the Q3 Renewals workspace'
                                }].map((tool, i) => <motion.div key={tool.name} initial={{
                                  opacity: 0,
                                  x: -5
                                }} animate={{
                                  opacity: 1,
                                  x: 0
                                }} transition={{
                                  delay: 0.6 + i * 0.12,
                                  duration: 0.3
                                }} className="flex items-center gap-2 text-[13px] sm:text-sm text-neutral-600" data-id="element-1696">
                                        <img src={`https://cdn.simpleicons.org/${tool.icon}/737373`} className="w-3.5 h-3.5" alt="" data-id="element-1697" />
                                        <span className="truncate" data-id="element-1698">
                                          <strong className="text-neutral-800 font-semibold" data-id="element-1699">
                                            {tool.name}
                                          </strong>{' '}
                                          {tool.text}
                                        </span>
                                      </motion.div>)}
                                  </div>
                                </div>
                              </div>
                            </motion.div>}
                        </AnimatePresence>

                        {/* Scroll anchor — keeps Giga's reply in view on send */}
                        <div ref={messagesEndRef} className="h-0" data-id="element-1700" />
                      </div>
                    </div>

                    <div className="relative p-4 sm:p-5" data-id="element-1701">
                      {/* Slack-style member-picker dropdown */}
                      <AnimatePresence data-id="element-1702">
                        {phase === 'dropdown' && <motion.div key="mention-dropdown" initial={{
                        opacity: 0,
                        y: 8,
                        scale: 0.98
                      }} animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1
                      }} exit={{
                        opacity: 0,
                        y: 8,
                        scale: 0.98
                      }} transition={{
                        duration: 0.18,
                        ease: 'easeOut'
                      }} className="absolute left-4 right-4 sm:left-5 sm:right-5 bottom-[calc(100%-0.5rem)] z-30 bg-white rounded-xl border border-neutral-200 shadow-xl overflow-hidden" data-id="element-1703">
                            <div className="px-3 pt-2.5 pb-1.5 text-[10px] sm:text-xs font-bold uppercase tracking-wider text-neutral-400" data-id="element-1704">
                              Members
                            </div>
                            {/* Highlighted Giga row */}
                            <div className="flex items-center gap-2.5 px-3 py-2 bg-[#2B211A]/10" data-id="element-1705">
                              <div className="w-7 h-7 rounded-md bg-[#EA580C] flex items-center justify-center shrink-0" data-id="element-1706">
                                <img src="https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png" alt="" className="w-4 h-4 object-contain [filter:brightness(0)_invert(1)]" data-id="element-1707" />
                              </div>
                              <div className="flex items-center gap-1.5 min-w-0" data-id="element-1708">
                                <span className="font-bold text-[#2B211A] text-sm truncate" data-id="element-1709">
                                  Giga
                                </span>
                                <span className="text-[9px] sm:text-[10px] font-bold bg-[#FFEDD5] text-[#EA580C] px-1 py-0.5 rounded" data-id="element-1710">
                                  APP
                                </span>
                              </div>
                              <span className="ml-auto text-xs text-neutral-400 hidden sm:block" data-id="element-1711">
                                works across your tools
                              </span>
                            </div>
                            {/* Dimmed member for realism */}
                            <div className="flex items-center gap-2.5 px-3 py-2" data-id="element-1712">
                              <div className="w-7 h-7 rounded-md bg-neutral-200 text-neutral-500 flex items-center justify-center font-bold text-xs shrink-0" data-id="element-1713">
                                GC
                              </div>
                              <span className="text-sm text-neutral-500 truncate" data-id="element-1714">
                                Gianna Cole
                              </span>
                            </div>
                          </motion.div>}
                      </AnimatePresence>

                      <div className="bg-neutral-50 border border-neutral-200 rounded-2xl px-5 py-3 sm:py-3.5 flex items-start gap-2 shadow-sm cursor-text hover:border-neutral-300 transition-colors" data-id="element-1715">
                        <span className="text-sm sm:text-base flex-1 min-w-0" data-id="element-1716">
                          {phase === 'idle' || phase === 'sent' || phase === 'thinking' || phase === 'response' ? <span className="text-neutral-400" data-id="element-1717">
                              Message Giga
                            </span> : <span className="text-neutral-800 break-words" data-id="element-1718">
                              {phase === 'mention' || phase === 'dropdown' ? trigger : <>
                                  <span className="text-[#1264A3] bg-[#1264A3]/10 rounded px-1 py-0.5 font-medium" data-id="element-1719">
                                    @Giga
                                  </span>
                                  {rest}
                                </>}
                              {!reducedMotion && phase !== 'done' && phase !== 'dropdown' && <motion.span aria-hidden="true" className="inline-block w-[2px] h-4 align-middle bg-[#EA580C] ml-0.5" animate={{
                            opacity: [1, 0]
                          }} transition={{
                            duration: 0.6,
                            repeat: Infinity
                          }} data-id="element-1720" />}
                            </span>}
                        </span>
                        <motion.div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center shrink-0 cursor-pointer" animate={typingDone ? {
                        backgroundColor: ['#2B211A', '#EA580C', '#2B211A'],
                        scale: [1, 0.82, 1.05, 1]
                      } : {
                        backgroundColor: '#2B211A',
                        scale: 1
                      }} transition={{
                        duration: 0.5,
                        ease: 'easeOut'
                      }} data-id="element-1721">
                          <Send size={14} className="text-white -ml-0.5 sm:w-4 sm:h-4" data-id="element-1722" />
                        </motion.div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* MOBILE SCENE (below md) */}
        <div className="md:hidden mt-8 w-full" data-id="element-1723">
          <div className="w-full bg-white rounded-2xl overflow-hidden border border-neutral-200 shadow-xl" data-id="element-1724">
            {/* Slack Chrome Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-neutral-50 border-b border-neutral-100" data-id="element-1725">
              <div className="flex gap-1.5" data-id="element-1726">
                <div className="w-3 h-3 rounded-full bg-neutral-300" data-id="element-1727" />
                <div className="w-3 h-3 rounded-full bg-neutral-300" data-id="element-1728" />
                <div className="w-3 h-3 rounded-full bg-neutral-300" data-id="element-1729" />
              </div>
              <div className="flex items-center gap-1.5 text-neutral-600 text-sm font-medium" data-id="element-1730">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1731">
                  <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" fill="#E01E5A" data-id="element-1732" />
                  <path d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.527 2.527 0 0 1 2.521 2.521 2.527 2.527 0 0 1-2.521 2.521H2.522A2.527 2.527 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z" fill="#36C5F0" data-id="element-1733" />
                  <path d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522v-2.521zm-1.272 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.161 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#2EB67D" data-id="element-1734" />
                  <path d="M15.161 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.161 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.272a2.527 2.527 0 0 1-2.52-2.521 2.526 2.526 0 0 1 2.52-2.522h6.317A2.527 2.527 0 0 1 24 15.163a2.528 2.528 0 0 1-2.522 2.521h-6.317z" fill="#ECB22E" data-id="element-1735" />
                </svg>
                Slack
              </div>
              <div className="w-8" data-id="element-1736" />
            </div>

            {/* Channel Header */}
            <div className="px-4 py-3 border-b border-neutral-100 flex items-center justify-between" data-id="element-1737">
              <div className="flex items-center gap-2" data-id="element-1738">
                <div className="w-2.5 h-2.5 rounded-full bg-[#F97316]" data-id="element-1739" />
                <span className="font-bold text-[#2B211A] text-sm" data-id="element-1740">Giga</span>
                <span className="text-[10px] font-bold bg-[#FFEDD5] text-[#EA580C] px-1.5 py-0.5 rounded" data-id="element-1741">
                  APP
                </span>
              </div>
              <span className="text-xs text-neutral-400" data-id="element-1742">Acme</span>
            </div>

            {/* Mobile Thread — Staged Reveal */}
            <div className="p-4 flex flex-col gap-6" data-id="element-1743">
              {/* Message (Priya mentions Giga) — preview starts here */}
              <motion.div initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-10%'
            }} transition={{
              duration: 0.5,
              delay: reducedMotion ? 0 : 0.2
            }} className="flex gap-3" data-id="element-1744">
                <img src="https://cdn.magicpatterns.com/uploads/74dN9xBfAH2PfDiwLgxxoL/CleanShot_2026-05-20_at_21.29.242x.png" alt="Faith" className="w-9 h-9 rounded-lg object-cover shrink-0" data-id="element-1745" />
                <div className="min-w-0 flex-1" data-id="element-1746">
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-1747">
                    <span className="font-bold text-[#333333] text-sm" data-id="element-1748">
                      Faith
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-1749">1:50 PM</span>
                  </div>
                  <p className="text-neutral-700 text-sm leading-relaxed" data-id="element-1750">
                    <span className="text-[#1264A3] bg-[#1264A3]/10 rounded px-1 py-0.5 font-medium mr-1" data-id="element-1751">
                      @Giga
                    </span>
                    {REST_MSG}
                  </p>
                </div>
              </motion.div>

              {/* Giga replies */}
              <motion.div initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-10%'
            }} transition={{
              duration: 0.5,
              delay: reducedMotion ? 0 : 1.0
            }} className="flex gap-3" data-id="element-1752">
                <div className="w-9 h-9 rounded-lg bg-[#EA580C] flex items-center justify-center shrink-0 shadow-sm" data-id="element-1753">
                  <img src="https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png" alt="Giga" className="w-6 h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-1754" />
                </div>
                <div className="min-w-0 flex-1" data-id="element-1755">
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-1756">
                    <span className="font-bold text-[#333333] text-sm" data-id="element-1757">
                      Giga
                    </span>
                    <span className="text-[10px] font-bold bg-[#FFEDD5] text-[#EA580C] px-1.5 py-0.5 rounded" data-id="element-1758">
                      APP
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-1759">1:50 PM</span>
                  </div>

                  <div className="text-sm text-neutral-800 leading-relaxed mt-1" data-id="element-1760">
                    On it — pulled the Northwind account from HubSpot and
                    drafted the renewal brief in Notion. It's attached to your
                    2pm call notes, with the open API risk{' '}
                    <span className="font-semibold text-[#2B211A]" data-id="element-1761">
                      (ENG-402)
                    </span>{' '}
                    flagged so you can lead with the extension offer.
                    <div className="flex items-center gap-2 mt-3" data-id="element-1762">
                      <div className="inline-flex items-center gap-1.5 bg-white border border-neutral-200 rounded-full px-2 py-1 shadow-sm" data-id="element-1763">
                        <img src="https://cdn.simpleicons.org/hubspot" alt="" className="w-3 h-3" data-id="element-1764" />
                        <span className="text-[10px] font-bold text-neutral-600" data-id="element-1765">
                          HubSpot
                        </span>
                      </div>
                      <div className="inline-flex items-center gap-1.5 bg-white border border-neutral-200 rounded-full px-2 py-1 shadow-sm" data-id="element-1766">
                        <img src="https://cdn.simpleicons.org/notion" alt="" className="w-3 h-3" data-id="element-1767" />
                        <span className="text-[10px] font-bold text-neutral-600" data-id="element-1768">
                          Notion
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}